import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, CheckCircle, ArrowRight, Clock, Shield, Award,
  Building, Factory, Store, Briefcase, GraduationCap, Hotel, HardHat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { industries, cities, services } from '../data/seoPages';

const iconMap = {
  healthcare: Building,
  warehouse: Factory,
  retail: Store,
  office: Briefcase,
  education: GraduationCap,
  hospitality: Hotel,
  construction: HardHat
};

export default function IndustryLocationPage() {
  const { slug } = useParams();
  
  // Find matching industry and city
  let industry = null;
  let city = null;
  
  for (const i of industries) {
    for (const c of cities) {
      if (slug === `${i.slug}-${c.slug}`) {
        industry = i;
        city = c;
        break;
      }
    }
    if (industry) break;
  }

  // Update document title and meta tags
  useEffect(() => {
    if (industry && city) {
      const pageTitle = `${industry.name} Low Voltage Solutions ${city.name}, FL | IQES`;
      const metaDescription = `${industry.description} in ${city.name}, Florida. CCTV, access control, networking. Licensed contractor. Free quotes. Call 786-981-3760.`;
      
      document.title = pageTitle;
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', metaDescription);
      }
    }
  }, [industry, city, slug]);
  
  if (!industry || !city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800">Page Not Found</h1>
          <Link to="/" className="text-orange-500 hover:underline mt-4 block">Return to Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[industry.slug] || Building;

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0a1628] via-[#1a2d4a] to-[#0f2341] text-white py-20 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full mb-6">
              <IconComponent className="w-4 h-4" />
              <span className="text-sm font-medium">{industry.name} Solutions</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {industry.name} Low Voltage in <span className="text-orange-500">{city.name}</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {industry.description} in {city.name}, Florida. Professional installation by licensed technicians.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quote">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+17869813760">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="mr-2 w-5 h-5" />
                  786-981-3760
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {industry.name} Solutions We Provide
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Specialized low voltage solutions designed for {industry.name.toLowerCase()} environments in {city.name}.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {industry.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{feature}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services for this Industry */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Services for {industry.name} in {city.name}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {services.slice(0, 6).map((service) => (
              <Link 
                key={service.slug}
                to={`/${service.slug}-${city.slug}`}
                className="bg-gray-50 p-6 rounded-xl hover:bg-orange-50 transition-colors group"
              >
                <h3 className="font-bold text-lg text-gray-800 group-hover:text-orange-500 mb-2">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Why Choose IQES for Your {industry.name} Project?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">{industry.name} Experience</h3>
                <p className="text-gray-600">Specialized experience in {industry.name.toLowerCase()} environments</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Compliance Ready</h3>
                <p className="text-gray-600">Installations that meet industry regulations</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="font-bold text-xl mb-2">Minimal Disruption</h3>
                <p className="text-gray-600">Work around your schedule</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              {industry.name} Services in Other Cities
            </h2>
            
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {cities.map((c) => (
                <Link 
                  key={c.slug}
                  to={`/${industry.slug}-${c.slug}`}
                  className={`px-4 py-2 rounded-full transition-all ${
                    c.slug === city.slug 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-orange-100'
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need {industry.name} Low Voltage Solutions?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Contact us today for a free consultation in {city.name}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+17869813760">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />
                Call 786-981-3760
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
